import { useState, useEffect, useRef, useCallback } from 'react';
import {
  File,
  FileText,
  AlertCircle,
  ListRestart,
  Loader2,
  CheckCircle,
  XCircle,
} from 'lucide-react';
import { useFiles } from '@/contexts/FilesContext';
import { FilterDropdown, TruncatedText, Pagination } from './PDFList/index';
interface FileItem {
  folderID: string | number;
  filePath: string;
  status?: 'processing' | 'success' | 'failed' | 'pending';
  metadata?: {
    title?: string;
    keywords?: string[];
    summary?: string;
  };
}
interface PDFListProps {
  projectId?: string | null;
}
// 将FilterDropdownWrapper移到组件外部，避免每次渲染都重新创建
const FilterDropdownWrapper = ({
  columnKey,
  title,
  placeholder,
  columnData,
  activeDropdown,
  columnFilters,
  selectedItems,
  searchTexts,
  setActiveDropdown,
  setColumnData,
  setSelectedItems,
  setSearchTexts,
  getColumnValues,
  applyColumnFilter,
  dropdownRefs,
}: {
  columnKey: string;
  title: string;
  placeholder: string;
  columnData: { [key: string]: string[] };
  activeDropdown: string | null;
  columnFilters: { [key: string]: string[] };
  selectedItems: { [key: string]: Set<string> };
  searchTexts: { [key: string]: string };
  setActiveDropdown: (value: string | null) => void;
  setColumnData: (value: any) => void;
  setSelectedItems: (value: any) => void;
  setSearchTexts: (value: any) => void;
  getColumnValues: (key: string) => string[];
  applyColumnFilter: (key: string) => void;
  dropdownRefs: React.MutableRefObject<{
    [key: string]: HTMLDivElement | null;
  }>;
}) => {
  return (
    <FilterDropdown
      columnKey={columnKey}
      title={title}
      placeholder={placeholder}
      allItems={columnData[columnKey] || []}
      isActive={activeDropdown === columnKey}
      hasFilters={(columnFilters[columnKey]?.length || 0) > 0}
      currentSelected={selectedItems[columnKey] || new Set()}
      searchText={searchTexts[columnKey] || ''}
      setSearchText={(value: string) =>
        setSearchTexts((prev: Record<string, string>) => ({
          ...prev,
          [columnKey]: value,
        }))
      }
      onToggle={() => {
        if (activeDropdown === columnKey) {
          setActiveDropdown(null);
        } else {
          setActiveDropdown(columnKey);
          setColumnData((prev: Record<string, string[]>) => ({
            ...prev,
            [columnKey]: getColumnValues(columnKey),
          }));
          setSelectedItems((prev: Record<string, Set<string>>) => ({
            ...prev,
            [columnKey]:
              prev[columnKey] || new Set(columnFilters[columnKey] || []),
          }));
        }
      }}
      onToggleItem={(value: string) => {
        setSelectedItems((prev: Record<string, Set<string>>) => {
          const currentSelected = prev[columnKey] || new Set();
          const newSelected = new Set(currentSelected);
          if (newSelected.has(value)) {
            newSelected.delete(value);
          } else {
            newSelected.add(value);
          }
          return { ...prev, [columnKey]: newSelected };
        });
      }}
      onSelectAll={(isAllSelected: boolean, filteredItems: string[]) => {
        setSelectedItems((prev: Record<string, Set<string>>) => {
          const currentSelected = prev[columnKey] || new Set();
          const newSelected = new Set(currentSelected);
          if (isAllSelected) {
            filteredItems.forEach(item => newSelected.delete(item));
          } else {
            filteredItems.forEach(item => newSelected.add(item));
          }
          return { ...prev, [columnKey]: newSelected };
        });
      }}
      onApply={() => applyColumnFilter(columnKey)}
      onCancel={() => setActiveDropdown(null)}
      dropdownRef={(el: HTMLDivElement | null) =>
        (dropdownRefs.current[columnKey] = el)
      }
    />
  );
};
export default function PDFList({ projectId }: PDFListProps) {
  const { files: allFiles, totalItems, isFilesLoading, loadFiles } = useFiles();
  const [error, setError] = useState<string | null>(null);
  const hasFetched = useRef(false);
  // 前端分页和筛选状态
  const [filteredFiles, setFilteredFiles] = useState<FileItem[]>([]);
  const [paginatedFiles, setPaginatedFiles] = useState<FileItem[]>([]);
  // Filter states
  const [columnFilters, setColumnFilters] = useState<{
    [key: string]: string[];
  }>({});
  const [activeDropdown, setActiveDropdown] = useState<string | null>(null);
  const [columnData, setColumnData] = useState<{ [key: string]: string[] }>({});
  const [selectedItems, setSelectedItems] = useState<{
    [key: string]: Set<string>;
  }>({});
  const [searchTexts, setSearchTexts] = useState<{ [key: string]: string }>({});
  // Pagination states
  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(50);
  const [jumpToPage, setJumpToPage] = useState('');
  // Table resize functionality
  const tableRef = useRef<HTMLTableElement>(null);
  const dropdownRefs = useRef<{ [key: string]: HTMLDivElement | null }>({});
  // Initial data load
  useEffect(() => {
    const fetchInitialFiles = async () => {
      if (!hasFetched.current) {
        hasFetched.current = true;
        try {
          setError(null);
          await loadFiles(1, null, projectId || null);
        } catch (err) {
          console.error('Initial fetch error:', err);
          setError(err instanceof Error ? err.message : 'Failed to load files');
        }
      }
    };
    fetchInitialFiles();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);
  // Setup global refresh function for UPLOAD_FINISH event
  useEffect(() => {
    const refreshPDFList = async () => {
      try {
        setError(null);
        await loadFiles(1, null, projectId || null);
      } catch (err) {
        console.error('Refresh error:', err);
        setError(
          err instanceof Error ? err.message : 'Failed to refresh files'
        );
      }
    };
    window.refreshPDFList = refreshPDFList;
    return () => {
      delete window.refreshPDFList;
    };
  }, [loadFiles, projectId]);
  // 筛选文件的逻辑
  useEffect(() => {
    let filtered = [...allFiles];
    // 应用下拉框筛选条件
    if (Object.keys(columnFilters).length > 0) {
      filtered = filtered.filter(file => {
        return Object.entries(columnFilters).every(
          ([columnKey, selectedValues]) => {
            if (!selectedValues || selectedValues.length === 0) return true;
            let fieldValue = '';
            switch (columnKey) {
              case 'fileName':
                fieldValue = extractFileName(file.filePath);
                break;
            }
            return selectedValues.some(selectedValue => {
              if (fieldValue === selectedValue) return true;
              if (fieldValue === 'N/A' || selectedValue === 'N/A') {
                return fieldValue === selectedValue;
              }
              return (
                fieldValue &&
                selectedValue &&
                fieldValue.includes(selectedValue)
              );
            });
          }
        );
      });
    }
    setFilteredFiles(filtered);
  }, [allFiles, columnFilters]);
  // 分页逻辑
  useEffect(() => {
    const startIndex = (currentPage - 1) * pageSize;
    const endIndex = startIndex + pageSize;
    const paginated = filteredFiles.slice(startIndex, endIndex);
    setPaginatedFiles(paginated);
  }, [filteredFiles, currentPage, pageSize]);
  // Table resize functionality
  useEffect(() => {
    const table = tableRef.current;
    if (!table) return;
    const resizeHandles = table.querySelectorAll('.resize-handle');
    const handlers: { element: Element; handler: (e: Event) => void }[] = [];
    resizeHandles.forEach((handle, index) => {
      const handleMouseDown = (e: Event) => {
        e.preventDefault();
        const mouseEvent = e as MouseEvent;
        const startX = mouseEvent.clientX;
        const col = table.querySelector(
          `colgroup col:nth-child(${index + 1})`
        ) as HTMLElement;
        if (!col) return;
        const startWidth = col.offsetWidth;
        const allCols = Array.from(
          table.querySelectorAll('colgroup col')
        ) as HTMLElement[];
        const initialWidths = allCols.map(c => c.offsetWidth);
        const totalInitialWidth = initialWidths.reduce((sum, w) => sum + w, 0);
        const handleMouseMove = (e: MouseEvent) => {
          const deltaX = e.clientX - startX;
          const newWidth = Math.max(100, startWidth + deltaX);
          const widthDelta = newWidth - startWidth;
          col.style.width = `${newWidth}px`;
          const newTableWidth = Math.max(800, totalInitialWidth + widthDelta);
          table.style.width = `${newTableWidth}px`;
          table.style.minWidth = `${newTableWidth}px`;
        };
        const handleMouseUp = () => {
          document.removeEventListener('mousemove', handleMouseMove);
          document.removeEventListener('mouseup', handleMouseUp);
        };
        document.addEventListener('mousemove', handleMouseMove);
        document.addEventListener('mouseup', handleMouseUp);
      };
      handle.addEventListener('mousedown', handleMouseDown);
      handlers.push({ element: handle, handler: handleMouseDown });
    });
    return () => {
      handlers.forEach(({ element, handler }) => {
        element.removeEventListener('mousedown', handler);
      });
    };
  }, [allFiles]);
  // Refresh 按钮处理函数
  const handleRefresh = async () => {
    try {
      setError(null);
      setColumnFilters({});
      setSelectedItems({});
      setSearchTexts({});
      setActiveDropdown(null);
      setCurrentPage(1);
      await loadFiles(1, null, projectId || null);
    } catch (err) {
      console.error('Refresh error:', err);
      setError(err instanceof Error ? err.message : 'Failed to load files');
    }
  };
  // Column filter functions
  const getColumnValues = useCallback(
    (columnKey: string): string[] => {
      const otherFilters = Object.entries(columnFilters).filter(
        ([key]) => key !== columnKey && columnFilters[key]?.length > 0
      );
      let filesToProcess = allFiles;
      if (otherFilters.length > 0) {
        filesToProcess = allFiles.filter(file => {
          return otherFilters.every(([otherColumnKey, selectedValues]) => {
            let fieldValue = '';
            switch (otherColumnKey) {
              case 'fileName':
                fieldValue = extractFileName(file.filePath);
                break;
            }
            return selectedValues.some(selectedValue => {
              if (fieldValue === selectedValue) return true;
              if (fieldValue === 'N/A' || selectedValue === 'N/A') {
                return fieldValue === selectedValue;
              }
              return (
                fieldValue &&
                selectedValue &&
                fieldValue.includes(selectedValue)
              );
            });
          });
        });
      }
      const values = new Set<string>();
      filesToProcess.forEach(file => {
        let value = '';
        switch (columnKey) {
          case 'fileName':
            value = extractFileName(file.filePath);
            break;
        }
        if (value.trim()) {
          values.add(value);
        }
      });
      return Array.from(values).sort();
    },
    [allFiles, columnFilters]
  );
  const applyColumnFilter = useCallback(
    (columnKey: string) => {
      const selectedValues = Array.from(selectedItems[columnKey] || new Set());
      setColumnFilters(prev => ({
        ...prev,
        [columnKey]: selectedValues,
      }));
      setActiveDropdown(null);
      setCurrentPage(1);
    },
    [selectedItems]
  );
  // Close dropdown when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (activeDropdown) {
        const dropdownElement = dropdownRefs.current[activeDropdown];
        if (
          dropdownElement &&
          !dropdownElement.contains(event.target as Node)
        ) {
          setActiveDropdown(null);
        }
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [activeDropdown]);
  // Pagination handlers
  const handlePageChange = (page: number) => {
    setCurrentPage(page);
  };
  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize);
    setCurrentPage(1);
  };
  const handleJumpToPage = () => {
    const pageNum = parseInt(jumpToPage);
    const maxPage = Math.ceil(filteredFiles.length / pageSize);
    if (pageNum >= 1 && pageNum <= maxPage) {
      setCurrentPage(pageNum);
      setJumpToPage('');
    } else {
      setJumpToPage('');
    }
  };
  const handleJumpInputKeyDown = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter') {
      handleJumpToPage();
    }
  };
  const handleFileClick = (file: FileItem) => {
    const apiUrl = `/api/files/retrieve?filePath=${file.filePath}&folderID=${file.folderID}`;
    const pdfViewerUrl = `/pdfjs/web/viewer.html?file=${encodeURIComponent(apiUrl)}`;
    const newWindow = window.open(pdfViewerUrl, '_blank');
    if (!newWindow) {
      console.warn('Pop-up blocked. Opening PDF viewer in current tab.');
      window.location.href = pdfViewerUrl;
    }
  };
  const extractFileName = (filePath: string) => {
    if (!filePath) return 'N/A';
    const parts = filePath.split(/[/\\]/);
    return parts[parts.length - 1] || filePath;
  };
  const renderStatus = (
    status?: 'processing' | 'success' | 'failed' | 'pending'
  ) => {
    if (!status) {
      return <span className="text-gray-500">N/A</span>;
    }
    switch (status) {
      case 'pending':
        return (
          <div className="flex items-center justify-center gap-1.5">
            <Loader2 className="h-4 w-4 text-blue-600 animate-spin" />
            <span className="text-blue-600 text-sm font-medium">pending</span>
          </div>
        );
      case 'processing':
        return (
          <div className="flex items-center justify-center gap-1.5">
            <Loader2 className="h-4 w-4 text-blue-600 animate-spin" />
            <span className="text-blue-600 text-sm font-medium">
              Processing
            </span>
          </div>
        );
      case 'success':
        return (
          <div className="flex items-center justify-center gap-1.5">
            <CheckCircle className="h-4 w-4 text-green-600" />
            <span className="text-green-600 text-sm font-medium">Success</span>
          </div>
        );
      case 'failed':
        return (
          <div className="flex items-center justify-center gap-1.5">
            <XCircle className="h-4 w-4 text-red-600" />
            <span className="text-red-600 text-sm font-medium">Failed</span>
          </div>
        );
      default:
        return <span className="text-gray-500">{status}</span>;
    }
  };
  return (
    <div className="flex flex-col h-full bg-gray-50 relative">
      {/* Header */}
      <div className="flex items-center justify-between px-6 py-2 border-b border-gray-200 bg-white relative">
        <h1 className="text-xl font-semibold text-gray-800">File List</h1>
        <div className="flex items-center gap-2">
          <button
            onClick={handleRefresh}
            className="flex items-center gap-2 px-3 py-1.5 text-sm bg-blue-600 text-white hover:bg-blue-700 rounded transition-colors"
          >
            <ListRestart size={14} />
            Reset
          </button>
        </div>
      </div>
      {/* Content */}
      <div className="flex-1 overflow-auto">
        {isFilesLoading ? (
          <div className="flex items-center justify-center h-64">
            <div className="flex items-center gap-3">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-gray-600"></div>
              <span className="text-gray-600">Loading PDF files...</span>
            </div>
          </div>
        ) : error ? (
          <div className="flex items-center justify-center h-64">
            <div className="text-center">
              <AlertCircle className="h-12 w-12 text-red-500 mx-auto mb-4" />
              <h2 className="text-lg font-medium text-gray-900 mb-2">
                Error Loading Files
              </h2>
              <p className="text-gray-600 mb-4">{error}</p>
              <button
                onClick={handleRefresh}
                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
              >
                Try Again
              </button>
            </div>
          </div>
        ) : (
          <div className="h-[calc(100vh-120px)] bg-white border border-gray-200 shadow-sm pb-6 px-2 relative">
            <div
              className="overflow-auto h-full px-2"
              style={{ width: '100%', minWidth: 0 }}
            >
              <table
                ref={tableRef}
                className="table-fixed"
                style={{ width: '100%', minWidth: '800px' }}
              >
                <colgroup>
                  <col style={{ width: '60px', minWidth: '60px' }} />
                  <col style={{ width: '120px', minWidth: '120px' }} />
                  <col
                    style={{ width: 'calc(100% - 180px)', minWidth: '150px' }}
                  />
                </colgroup>
                <thead className="bg-gray-50 sticky top-0 z-10">
                  <tr>
                    <th className="px-2 py-1.5 text-sm font-semibold text-gray-800 tracking-wider border-b border-gray-200 border-r">
                      No.
                    </th>
                    <th className="px-2 py-1.5 text-sm font-semibold text-gray-800 tracking-wider border-b border-gray-200 border-r">
                      Status
                    </th>
                    <th className="px-4 py-1.5 text-left text-sm font-semibold text-gray-800 tracking-wider border-b border-gray-200 border-r relative">
                      <FilterDropdownWrapper
                        columnKey="fileName"
                        title="File Name"
                        placeholder="Filter by file name..."
                        selectedItems={selectedItems}
                        columnData={columnData}
                        activeDropdown={activeDropdown}
                        columnFilters={columnFilters}
                        searchTexts={searchTexts}
                        setSearchTexts={setSearchTexts}
                        setActiveDropdown={setActiveDropdown}
                        setColumnData={setColumnData}
                        setSelectedItems={setSelectedItems}
                        applyColumnFilter={applyColumnFilter}
                        getColumnValues={getColumnValues}
                        dropdownRefs={dropdownRefs}
                      />
                      <div className="absolute top-0 right-0 w-1 h-full cursor-col-resize bg-transparent hover:bg-blue-300 resize-handle"></div>
                    </th>
                  </tr>
                </thead>
                <tbody className="bg-white divide-y divide-gray-100">
                  {paginatedFiles.length === 0 ? (
                    <tr>
                      <td colSpan={3} className="px-4 py-16 text-center">
                        <div className="flex flex-col items-center justify-center">
                          {error ? (
                            <>
                              <AlertCircle className="h-12 w-12 text-red-500 mb-4" />
                              <h2 className="text-lg font-medium text-gray-900 mb-2">
                                Request Failed
                              </h2>
                              <p className="text-gray-600 mb-4">{error}</p>
                              <button
                                onClick={handleRefresh}
                                className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
                              >
                                Try Again
                              </button>
                            </>
                          ) : (
                            <>
                              <File className="h-12 w-12 text-gray-400 mb-4" />
                              <h2 className="text-lg font-medium text-gray-900 mb-2">
                                No PDF Files Found
                              </h2>
                              <p className="text-gray-600">
                                There are no PDF files available for preview.
                              </p>
                            </>
                          )}
                        </div>
                      </td>
                    </tr>
                  ) : (
                    paginatedFiles.map((file, index) => (
                      <tr
                        key={`${file.folderID}-${file.filePath}-${index}`}
                        className="hover:bg-gray-50 transition-colors"
                      >
                        <td className="px-2 py-2 border-r border-gray-100 text-center text-sm text-gray-500">
                          {(currentPage - 1) * pageSize + index + 1}
                        </td>
                        <td className="px-2 py-2 border-r border-gray-100 text-center text-sm">
                          {renderStatus(file.status)}
                        </td>
                        <td className="px-4 py-2 border-r border-gray-100">
                          <div className="flex items-start gap-2">
                            <FileText className="h-4 w-4 text-blue-400 flex-shrink-0 mt-0.5" />
                            <div className="min-w-0 flex-1">
                              <div
                                className="font-medium text-blue-600 text-sm cursor-pointer hover:text-blue-800 hover:underline"
                                onClick={() => handleFileClick(file)}
                              >
                                <TruncatedText
                                  text={extractFileName(file.filePath)}
                                />
                              </div>
                            </div>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
              <div className="h-4"></div>
            </div>
          </div>
        )}
      </div>
      {/* Pagination */}
      <Pagination
        currentPage={currentPage}
        pageSize={pageSize}
        totalItems={totalItems}
        jumpToPage={jumpToPage}
        onPageChange={handlePageChange}
        onPageSizeChange={handlePageSizeChange}
        onJumpToPage={handleJumpToPage}
        onJumpInputChange={setJumpToPage}
        onJumpInputKeyDown={handleJumpInputKeyDown}
        isLoading={isFilesLoading}
      />
    </div>
  );
}
